// Human Resources application messages (Uzbek default, Russian and English).
export const hrMessages = {
  en: {
    hr: {
      appTitle: 'Human Resources',
      appDescription: 'Employees, attendance, leave, and overtime.',
      nav: {
        overview: 'Overview',
        employees: 'Employees',
        people: 'People',
        time: 'Time & attendance',
        attendance: 'Attendance',
        attendanceReport: 'Monthly report',
        leave: 'Leave',
        overtime: 'Overtime',
      },
      common: {
        actions: 'Actions',
        open: 'Open',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        create: 'Create',
        edit: 'Edit',
        delete: 'Delete',
        retry: 'Retry',
        refresh: 'Refresh',
        loading: 'Loading…',
        empty: 'Nothing to show.',
        search: 'Search',
        searchPlaceholder: 'Search by name, code or job title…',
        allDepartments: 'All departments & warehouses',
        allTypes: 'All types',
        allStatuses: 'All statuses',
        apply: 'Apply',
        reset: 'Reset',
        department: 'Department',
        employee: 'Employee',
        employeeCode: 'Employee code',
        jobTitle: 'Job title',
        status: 'Status',
        date: 'Date',
        from: 'From',
        to: 'To',
        notes: 'Notes',
        hours: 'Hours',
        cycles: 'Cycles',
        amount: 'Amount',
        currency: 'Currency',
        total: '{count} rows',
        page: 'Page {page} of {total}',
        previous: 'Previous',
        next: 'Next',
        back: 'Back',
        close: 'Close',
        optional: 'optional',
        yes: 'Yes',
        no: 'No',
        active: 'Active',
        inactive: 'Inactive',
        all: 'All',
        none: 'None',
        noPermission: 'You do not have permission for this action.',
        invalidRange: 'The start date must not be after the end date.',
      },
      employeeType: {
        office: 'Office',
        production: 'Production',
        mixed: 'Mixed',
      },
      billingMode: {
        hourly: 'Hourly',
        production_cycle: 'Per production cycle',
        fixed_salary: 'Fixed salary',
        fixed_task: 'Fixed task',
      },
      attendanceStatus: {
        present: 'Present',
        absent: 'Absent',
        late: 'Late',
        leave: 'Leave',
        remote: 'Remote',
        half_day: 'Half day',
      },
      entryType: {
        office_task: 'Office task',
        production_cycle: 'Production cycle',
        admin_task: 'Administrative task',
      },
      billingUnit: {
        hour: 'Per hour',
        cycle: 'Per cycle',
        fixed: 'Fixed amount',
      },
      overview: {
        eyebrow: 'Human resources',
        title: 'Overview',
        description: 'Workforce headcount, today’s attendance position and hours logged this month.',
        loading: 'Loading overview…',
        error: 'Failed to load the overview.',
        headcount: 'Employees',
        active: 'Active employees',
        departments: 'Departments',
        presentToday: 'Present today',
        absentToday: 'Absent today',
        onLeaveToday: 'On leave today',
        hoursThisMonth: 'Hours this month',
        overtimeHours: 'Overtime hours',
        todaySnapshot: 'Today’s attendance',
        noToday: 'No attendance recorded today yet.',
        topPerformers: 'Most hours this month',
        noPerformers: 'No hours logged this month.',
        quickActions: 'Quick actions',
        addEmployee: 'Add employee',
        openAttendance: 'Record attendance',
        openLeave: 'Leave register',
        openOvertime: 'Overtime & work hours',
        records: 'records',
      },
      employees: {
        eyebrow: 'People',
        title: 'Employees',
        description: 'Workforce register with employment type, department, and billing configuration.',
        newEmployee: 'New employee',
        loading: 'Loading employees…',
        error: 'Failed to load employees.',
        empty: 'No employees match the filters.',
        name: 'Name',
        code: 'Code',
        department: 'Department',
        jobTitle: 'Job title',
        type: 'Type',
        billing: 'Billing',
        hireDate: 'Hired',
        status: 'Status',
        view: 'Open',
        edit: 'Edit',
        delete: 'Delete',
        deleteConfirm: 'Delete this employee? Attendance and timesheet records cascade with the employee.',
        totals: '{total} employees · {active} active',
      },
      employeeForm: {
        createTitle: 'New employee',
        createDescription: 'Create a personnel record with employment and compensation defaults.',
        editTitle: 'Edit employee',
        editDescription: 'Update personnel, contact, and compensation details.',
        sectionIdentity: 'Employment',
        sectionContact: 'Contact',
        sectionPersonal: 'Personal & documents',
        sectionCompensation: 'Compensation defaults',
        codeAuto: 'Employee code {code} is assigned automatically.',
        firstName: 'First name',
        lastName: 'Last name',
        middleName: 'Middle name',
        department: 'Department / warehouse',
        departmentHint: 'Departments, internal teams, and warehouses.',
        supervisor: 'Supervisor',
        jobTitle: 'Job title',
        employeeType: 'Employment type',
        hireDate: 'Hire date',
        active: 'Active employee',
        email: 'Email',
        phone: 'Phone',
        homeAddress: 'Home address',
        emergencyName: 'Emergency contact',
        emergencyNumber: 'Emergency phone',
        birthday: 'Date of birth',
        passport: 'Passport number',
        taxId: 'Tax ID (INN)',
        bankCard: 'Bank card number',
        billingMode: 'Billing mode',
        hourlyRate: 'Hourly rate',
        cycleRate: 'Rate per cycle',
        fixedSalary: 'Fixed salary',
        currency: 'Currency',
        currencyFallback: 'Currency code',
        currencyHint: 'Three-letter ISO code of an active currency. Leave empty to use the company default.',
        currenciesUnavailable: 'The currency list could not be loaded — enter the three-letter code manually.',
        photo: 'Photo',
        photoHint: 'JPG, PNG, WebP or GIF up to 5 MB.',
        photoChoose: 'Choose photo',
        photoRemove: 'Remove photo',
        photoInvalidType: 'Only JPG, PNG, WebP or GIF images are supported.',
        photoTooLarge: 'The photo must not exceed 5 MB.',
        submitCreate: 'Create employee',
        submitUpdate: 'Save changes',
        created: 'Employee created.',
        updated: 'Employee saved.',
        invalidRequired: 'Employee code, first name, last name, employment type, and billing mode are required.',
        invalidRate: 'Rates must be zero or greater.',
        supervisorSelf: 'An employee cannot be their own supervisor.',
      },
      employeeDetail: {
        eyebrow: 'Employee',
        back: 'Back to employees',
        loading: 'Loading employee…',
        error: 'Failed to load the employee.',
        notFound: 'This employee could not be found.',
        profile: 'Profile',
        employment: 'Employment',
        contact: 'Contact',
        personal: 'Personal & documents',
        compensation: 'Compensation',
        linkedUser: 'System user',
        noLinkedUser: 'Not linked to a system user',
        supervisor: 'Supervisor',
        noSupervisor: 'No supervisor assigned',
        hireDate: 'Hire date',
        createdAt: 'Record created',
        edit: 'Edit employee',
        delete: 'Delete',
        deleteConfirm: 'Delete this employee? This cannot be undone.',
        deleteTitle: 'Delete employee',
        actionAttendance: 'Attendance',
        actionLeave: 'Leave',
        actionHours: 'Work hours',
        actionsLabel: 'Employee actions',
        tabProfile: 'Profile',
        tabAttendance: 'Attendance',
        tabHours: 'Work hours',
        tabDocuments: 'Documents',
        tabsLabel: 'Employee sections',
        recordAttendance: 'Record attendance',
        recordLeave: 'Record leave',
        recordOvertime: 'Log work hours',
        attendanceSummary: 'Attendance this month',
        timesheetSummary: 'Hours this month',
        recentAttendance: 'Recent attendance',
        recentTimesheets: 'Recent work hours',
        noAttendance: 'No attendance records this month.',
        noTimesheets: 'No work hours logged this month.',
        daysAttended: 'Attended days',
        daysAbsent: 'Absent days',
        attendanceRate: 'Attendance rate',
        workedHours: 'Worked hours',
        totalHours: 'Total hours',
        entries: 'Entries',
        billAmount: 'Billable amount',
        rates: 'Rates',
        hourlyRate: 'Hourly',
        cycleRate: 'Per cycle',
        fixedSalary: 'Fixed salary',
      },
      employeeFiles: {
        title: 'Documents',
        description: 'Scanned documents attached to this employee: passport, contract, certificates, medical records.',
        count: '{count} files',
        category: 'Category',
        file: 'File',
        upload: 'Upload',
        uploading: 'Uploading…',
        uploadHint: 'Image, PDF, Word or Excel up to 20 MB.',
        uploaded: 'Document uploaded.',
        deleted: 'Document deleted.',
        delete: 'Delete',
        download: 'Download',
        deleteConfirm: 'Delete “{name}”? The stored file is removed from the server.',
        tooLarge: 'The file must not exceed 20 MB.',
        noFile: 'Choose a file to upload first.',
        empty: 'No documents uploaded yet.',
        loading: 'Loading documents…',
        name: 'File',
        size: 'Size',
        uploadedBy: 'Uploaded by',
        uploadedAt: 'Uploaded',
        categoryPassport: 'Passport',
        categoryContract: 'Contract',
        categoryCertificate: 'Certificate',
        categoryMedical: 'Medical',
        categoryOther: 'Other',
      },
      attendance: {
        eyebrow: 'Time & attendance',
        title: 'Attendance',
        description: 'Daily attendance register with check-in / check-out times and worked minutes.',
        newRecord: 'Record attendance',
        loading: 'Loading attendance…',
        error: 'Failed to load attendance.',
        empty: 'No attendance records match the filters.',
        employee: 'Employee',
        department: 'Department',
        date: 'Date',
        status: 'Status',
        checkIn: 'Check-in',
        checkOut: 'Check-out',
        worked: 'Worked',
        minutes: 'Worked minutes',
        notes: 'Notes',
        edit: 'Edit',
        delete: 'Delete',
        deleteConfirm: 'Delete this attendance record?',
        formTitleCreate: 'Record attendance',
        formTitleEdit: 'Edit attendance',
        created: 'Attendance recorded.',
        updated: 'Attendance updated.',
        deleted: 'Attendance record deleted.',
        invalidEmployee: 'Select an employee.',
        invalidDate: 'Select the attendance date.',
        duplicate: 'Attendance for this employee and date already exists.',
        summaryPresent: 'Present',
        summaryAbsent: 'Absent',
        summaryLate: 'Late',
        summaryLeave: 'On leave',
        summaryRemote: 'Remote',
        summaryHalfDay: 'Half day',
        filterStatus: 'Status',
        rangeFrom: 'From date',
        rangeTo: 'To date',
        minutesHint: 'Minutes are stored as recorded; leave empty to use the check-in / check-out times.',
        totals: '{count} records in range',
      },
      attendanceReport: {
        eyebrow: 'Time & attendance',
        title: 'Monthly attendance report',
        description: 'Per-employee attendance totals for the selected period, including attendance rate.',
        loading: 'Loading report…',
        error: 'Failed to load the attendance report.',
        empty: 'No attendance in this period.',
        employee: 'Employee',
        code: 'Code',
        totalDays: 'Days recorded',
        attendedDays: 'Attended',
        absentDays: 'Absent',
        workedHours: 'Worked hours',
        attendanceRate: 'Attendance rate',
        totals: 'Workforce totals',
        month: 'Month',
        department: 'Department',
        allDepartments: 'All departments & warehouses',
        recorded: 'Recorded days',
        averageRate: 'Average attendance rate',
        employeesCovered: 'Employees covered',
      },
      leave: {
        eyebrow: 'Time & attendance',
        title: 'Leave',
        description: 'Leave register derived from attendance records with status “leave”, plus per-employee leave usage.',
        period: 'Period',
        loading: 'Loading leave…',
        error: 'Failed to load leave records.',
        empty: 'No leave records in this period.',
        onLeaveToday: 'On leave today',
        noOnLeaveToday: 'Nobody is on leave today.',
        summaryTitle: 'Leave usage per employee',
        registerTitle: 'Leave register',
        newLeave: 'Record leave',
        month: 'Month',
        employee: 'Employee',
        department: 'Department',
        days: 'Leave days',
        lastLeave: 'Last leave day',
        date: 'Date',
        from: 'From',
        to: 'To',
        notes: 'Notes',
        record: 'Record',
        recordTitle: 'Record leave',
        recordHint: 'One attendance record per day is created with status “leave”.',
        created: 'Leave recorded for {count} day(s).',
        createdPartial: 'Leave recorded for {count} day(s). {skipped} day(s) already had a record.',
        noneCreated: 'Every selected day already has an attendance record.',
        invalidEmployee: 'Select an employee.',
        invalidRange: 'Select a valid leave period.',
        deleteConfirm: 'Delete this leave day?',
        deleted: 'Leave day deleted.',
        leaveDays: 'Leave days in period',
        employeesOnLeave: 'Employees on leave',
        daysUsed: 'Days used',
        noPermission: 'You do not need approval rights for this action — leave is recorded directly on attendance.',
      },
      overtime: {
        eyebrow: 'Time & attendance',
        title: 'Overtime & work hours',
        description: 'Work-hour entries with overtime derived from daily hours above the standard working day.',
        standardDay: 'Standard working day',
        standardHint: 'Daily hours above this threshold are counted as overtime.',
        hours: 'hours',
        loading: 'Loading work hours…',
        error: 'Failed to load work hours.',
        empty: 'No work-hour entries in this period.',
        totalHours: 'Hours logged',
        overtimeHours: 'Overtime hours',
        employeesAffected: 'Employees with overtime',
        overtimeEntries: 'Overtime days',
        ledgerTitle: 'Daily overtime ledger',
        summaryTitle: 'Overtime per employee',
        entriesTitle: 'Recent work-hour entries',
        employee: 'Employee',
        department: 'Department',
        workDate: 'Work date',
        entries: 'Entries',
        dayHours: 'Day hours',
        overtime: 'Overtime',
        task: 'Task',
        entryType: 'Entry type',
        billAmount: 'Billable amount',
        logHours: 'Log work hours',
        logTitle: 'Log work hours',
        created: 'Work hours logged.',
        deleted: 'Work-hour entry deleted.',
        invalidEmployee: 'Select an employee.',
        invalidDate: 'Select the work date.',
        invalidTask: 'Enter a task name.',
        invalidHours: 'Hours must be greater than zero.',
        deleteConfirm: 'Delete this work-hour entry?',
        noOvertime: 'No overtime in this period.',
        cycleUnit: 'cycles',
        totalOvertime: 'Total overtime',
        noEntries: 'No entries yet.',
        month: 'Month',
      },
      picker: {
        employeeTitle: 'Select employee',
        employeeEyebrow: 'Active employees',
        employeeSearch: 'Search employees',
        employeePlaceholder: 'Search by name, code or department',
        employeeEmpty: 'No employees found.',
        employeeLoading: 'Loading employees…',
        departmentTitle: 'Select department or warehouse',
        departmentEyebrow: 'Departments & warehouses',
        departmentSearch: 'Search departments and warehouses',
        departmentPlaceholder: 'Search by name or code',
        departmentEmpty: 'No departments or warehouses found.',
        departmentLoading: 'Loading departments and warehouses…',
        typeFilter: 'Type',
        typeAll: 'All types',
        typeDepartment: 'Department',
        typeInternal: 'Internal department',
        typeWarehouse: 'Warehouse',
        supervisorTitle: 'Select supervisor',
        supervisorEyebrow: 'Employees',
        supervisorSearch: 'Search supervisors',
        supervisorPlaceholder: 'Search by name or code',
        supervisorEmpty: 'No employees found.',
        supervisorSelf: 'This employee supervises themselves and cannot be selected.',
        page: 'Page {page} of {total}',
        pageRange: 'Page {page} of {total} · {count} items',
        close: 'Close picker',
        retry: 'Retry',
        previous: 'Previous',
        next: 'Next',
        cancel: 'Cancel',
      },
    },
  },
  ru: {
    hr: {
      appTitle: 'Персонал',
      appDescription: 'Сотрудники, посещаемость, отпуска и сверхурочные.',
      nav: {
        overview: 'Обзор',
        employees: 'Сотрудники',
        people: 'Персонал',
        time: 'Время и посещаемость',
        attendance: 'Посещаемость',
        attendanceReport: 'Месячный отчёт',
        leave: 'Отпуска',
        overtime: 'Сверхурочные',
      },
      common: {
        actions: 'Действия',
        open: 'Открыть',
        cancel: 'Отмена',
        save: 'Сохранить',
        saving: 'Сохранение…',
        create: 'Создать',
        edit: 'Изменить',
        delete: 'Удалить',
        retry: 'Повторить',
        refresh: 'Обновить',
        loading: 'Загрузка…',
        empty: 'Нет данных.',
        search: 'Поиск',
        searchPlaceholder: 'Поиск по имени, коду или должности…',
        allDepartments: 'Все подразделения и склады',
        allTypes: 'Все типы',
        allStatuses: 'Все статусы',
        apply: 'Применить',
        reset: 'Сбросить',
        department: 'Подразделение',
        employee: 'Сотрудник',
        employeeCode: 'Табельный номер',
        jobTitle: 'Должность',
        status: 'Статус',
        date: 'Дата',
        from: 'С',
        to: 'По',
        notes: 'Примечания',
        hours: 'Часы',
        cycles: 'Циклы',
        amount: 'Сумма',
        currency: 'Валюта',
        total: 'Строк: {count}',
        page: 'Страница {page} из {total}',
        previous: 'Назад',
        next: 'Вперёд',
        back: 'Назад',
        close: 'Закрыть',
        optional: 'необязательно',
        yes: 'Да',
        no: 'Нет',
        active: 'Активен',
        inactive: 'Неактивен',
        all: 'Все',
        none: 'Нет',
        noPermission: 'У вас нет прав на это действие.',
        invalidRange: 'Дата начала не может быть позже даты окончания.',
      },
      employeeType: {
        office: 'Офис',
        production: 'Производство',
        mixed: 'Смешанный',
      },
      billingMode: {
        hourly: 'Почасово',
        production_cycle: 'За производственный цикл',
        fixed_salary: 'Фиксированный оклад',
        fixed_task: 'Фиксированное задание',
      },
      attendanceStatus: {
        present: 'Присутствует',
        absent: 'Отсутствует',
        late: 'Опоздание',
        leave: 'Отпуск',
        remote: 'Удалённо',
        half_day: 'Полдня',
      },
      entryType: {
        office_task: 'Офисная задача',
        production_cycle: 'Производственный цикл',
        admin_task: 'Административная задача',
      },
      billingUnit: {
        hour: 'За час',
        cycle: 'За цикл',
        fixed: 'Фиксированная сумма',
      },
      overview: {
        eyebrow: 'Персонал',
        title: 'Обзор',
        description: 'Численность персонала, посещаемость за сегодня и отработанные часы за месяц.',
        loading: 'Загрузка обзора…',
        error: 'Не удалось загрузить обзор.',
        headcount: 'Сотрудники',
        active: 'Активные сотрудники',
        departments: 'Подразделения',
        presentToday: 'Присутствуют сегодня',
        absentToday: 'Отсутствуют сегодня',
        onLeaveToday: 'В отпуске сегодня',
        hoursThisMonth: 'Часы за месяц',
        overtimeHours: 'Сверхурочные часы',
        todaySnapshot: 'Посещаемость за сегодня',
        noToday: 'Сегодня посещаемость ещё не отмечена.',
        topPerformers: 'Больше всего часов за месяц',
        noPerformers: 'За этот месяц часы не вносились.',
        quickActions: 'Быстрые действия',
        addEmployee: 'Добавить сотрудника',
        openAttendance: 'Отметить посещаемость',
        openLeave: 'Реестр отпусков',
        openOvertime: 'Сверхурочные и часы',
        records: 'записей',
      },
      employees: {
        eyebrow: 'Персонал',
        title: 'Сотрудники',
        description: 'Реестр персонала с типом занятости, подразделением и настройками оплаты.',
        newEmployee: 'Новый сотрудник',
        loading: 'Загрузка сотрудников…',
        error: 'Не удалось загрузить сотрудников.',
        empty: 'Нет сотрудников по выбранным фильтрам.',
        name: 'Имя',
        code: 'Код',
        department: 'Подразделение',
        jobTitle: 'Должность',
        type: 'Тип',
        billing: 'Оплата',
        hireDate: 'Принят',
        status: 'Статус',
        view: 'Открыть',
        edit: 'Изменить',
        delete: 'Удалить',
        deleteConfirm: 'Удалить сотрудника? Записи посещаемости и табелей будут удалены вместе с ним.',
        totals: 'Сотрудников: {total} · активных: {active}',
      },
      employeeForm: {
        createTitle: 'Новый сотрудник',
        createDescription: 'Создайте кадровую запись с параметрами занятости и оплаты.',
        editTitle: 'Изменение сотрудника',
        editDescription: 'Обновите кадровые, контактные и расчётные данные.',
        sectionIdentity: 'Занятость',
        sectionContact: 'Контакты',
        sectionPersonal: 'Личные данные и документы',
        sectionCompensation: 'Настройки оплаты',
        codeAuto: 'Табельный номер {code} присваивается автоматически.',
        firstName: 'Имя',
        lastName: 'Фамилия',
        middleName: 'Отчество',
        department: 'Подразделение / склад',
        departmentHint: 'Подразделения, внутренние отделы и склады.',
        supervisor: 'Руководитель',
        jobTitle: 'Должность',
        employeeType: 'Тип занятости',
        hireDate: 'Дата приёма',
        active: 'Активный сотрудник',
        email: 'Эл. почта',
        phone: 'Телефон',
        homeAddress: 'Домашний адрес',
        emergencyName: 'Экстренный контакт',
        emergencyNumber: 'Телефон экстренного контакта',
        birthday: 'Дата рождения',
        passport: 'Номер паспорта',
        taxId: 'ИНН',
        bankCard: 'Номер банковской карты',
        billingMode: 'Режим оплаты',
        hourlyRate: 'Ставка за час',
        cycleRate: 'Ставка за цикл',
        fixedSalary: 'Фиксированный оклад',
        currency: 'Валюта',
        currencyFallback: 'Код валюты',
        currencyHint: 'Трёхбуквенный код активной валюты. Оставьте пустым, чтобы использовать валюту компании.',
        currenciesUnavailable: 'Список валют не загрузился — введите трёхбуквенный код вручную.',
        photo: 'Фотография',
        photoHint: 'JPG, PNG, WebP или GIF до 5 МБ.',
        photoChoose: 'Выбрать фото',
        photoRemove: 'Удалить фото',
        photoInvalidType: 'Поддерживаются только изображения JPG, PNG, WebP или GIF.',
        photoTooLarge: 'Размер фото не должен превышать 5 МБ.',
        submitCreate: 'Создать сотрудника',
        submitUpdate: 'Сохранить изменения',
        created: 'Сотрудник создан.',
        updated: 'Сотрудник сохранён.',
        invalidRequired: 'Обязательны: табельный номер, имя, фамилия, тип занятости и режим оплаты.',
        invalidRate: 'Ставки не могут быть отрицательными.',
        supervisorSelf: 'Сотрудник не может быть своим руководителем.',
      },
      employeeDetail: {
        eyebrow: 'Сотрудник',
        back: 'К списку сотрудников',
        loading: 'Загрузка сотрудника…',
        error: 'Не удалось загрузить сотрудника.',
        notFound: 'Сотрудник не найден.',
        profile: 'Профиль',
        employment: 'Занятость',
        contact: 'Контакты',
        personal: 'Личные данные и документы',
        compensation: 'Оплата',
        linkedUser: 'Пользователь системы',
        noLinkedUser: 'Не привязан к пользователю',
        supervisor: 'Руководитель',
        noSupervisor: 'Руководитель не назначен',
        hireDate: 'Дата приёма',
        createdAt: 'Запись создана',
        edit: 'Изменить сотрудника',
        delete: 'Удалить',
        deleteConfirm: 'Удалить сотрудника? Действие необратимо.',
        deleteTitle: 'Удаление сотрудника',
        actionAttendance: 'Посещаемость',
        actionLeave: 'Отпуск',
        actionHours: 'Рабочие часы',
        actionsLabel: 'Действия с сотрудником',
        tabProfile: 'Профиль',
        tabAttendance: 'Посещаемость',
        tabHours: 'Рабочие часы',
        tabDocuments: 'Документы',
        tabsLabel: 'Разделы сотрудника',
        recordAttendance: 'Отметить посещаемость',
        recordLeave: 'Оформить отпуск',
        recordOvertime: 'Внести рабочие часы',
        attendanceSummary: 'Посещаемость за месяц',
        timesheetSummary: 'Часы за месяц',
        recentAttendance: 'Последняя посещаемость',
        recentTimesheets: 'Последние рабочие часы',
        noAttendance: 'За этот месяц посещаемости нет.',
        noTimesheets: 'За этот месяц часы не вносились.',
        daysAttended: 'Дней присутствия',
        daysAbsent: 'Дней отсутствия',
        attendanceRate: 'Явка',
        workedHours: 'Отработанные часы',
        totalHours: 'Всего часов',
        entries: 'Записи',
        billAmount: 'Сумма к оплате',
        rates: 'Ставки',
        hourlyRate: 'Почасовая',
        cycleRate: 'За цикл',
        fixedSalary: 'Оклад',
      },
      employeeFiles: {
        title: 'Документы',
        description: 'Сканы документов сотрудника: паспорт, договор, сертификаты, медосмотр.',
        count: 'Файлов: {count}',
        category: 'Категория',
        file: 'Файл',
        upload: 'Загрузить',
        uploading: 'Загрузка…',
        uploadHint: 'Изображение, PDF, Word или Excel до 20 МБ.',
        uploaded: 'Документ загружен.',
        deleted: 'Документ удалён.',
        delete: 'Удалить',
        download: 'Скачать',
        deleteConfirm: 'Удалить «{name}»? Файл будет удалён с сервера.',
        tooLarge: 'Размер файла не должен превышать 20 МБ.',
        noFile: 'Сначала выберите файл.',
        empty: 'Документы ещё не загружены.',
        loading: 'Загрузка документов…',
        name: 'Файл',
        size: 'Размер',
        uploadedBy: 'Загрузил',
        uploadedAt: 'Загружен',
        categoryPassport: 'Паспорт',
        categoryContract: 'Договор',
        categoryCertificate: 'Сертификат',
        categoryMedical: 'Медосмотр',
        categoryOther: 'Другое',
      },
      attendance: {
        eyebrow: 'Время и посещаемость',
        title: 'Посещаемость',
        description: 'Ежедневный реестр посещаемости с приходом, уходом и отработанными минутами.',
        newRecord: 'Отметить посещаемость',
        loading: 'Загрузка посещаемости…',
        error: 'Не удалось загрузить посещаемость.',
        empty: 'Нет записей по выбранным фильтрам.',
        employee: 'Сотрудник',
        department: 'Подразделение',
        date: 'Дата',
        status: 'Статус',
        checkIn: 'Приход',
        checkOut: 'Уход',
        worked: 'Отработано',
        minutes: 'Отработанные минуты',
        notes: 'Примечания',
        edit: 'Изменить',
        delete: 'Удалить',
        deleteConfirm: 'Удалить запись посещаемости?',
        formTitleCreate: 'Отметить посещаемость',
        formTitleEdit: 'Изменение посещаемости',
        created: 'Посещаемость отмечена.',
        updated: 'Посещаемость обновлена.',
        deleted: 'Запись удалена.',
        invalidEmployee: 'Выберите сотрудника.',
        invalidDate: 'Выберите дату посещаемости.',
        duplicate: 'Запись для этого сотрудника и даты уже существует.',
        summaryPresent: 'Присутствует',
        summaryAbsent: 'Отсутствует',
        summaryLate: 'Опоздание',
        summaryLeave: 'Отпуск',
        summaryRemote: 'Удалённо',
        summaryHalfDay: 'Полдня',
        filterStatus: 'Статус',
        rangeFrom: 'Дата с',
        rangeTo: 'Дата по',
        minutesHint: 'Минуты сохраняются как есть; оставьте пустым, чтобы использовать время прихода и ухода.',
        totals: 'Записей в периоде: {count}',
      },
      attendanceReport: {
        eyebrow: 'Время и посещаемость',
        title: 'Месячный отчёт по посещаемости',
        description: 'Итоги посещаемости по сотрудникам за выбранный период с показателем явки.',
        loading: 'Загрузка отчёта…',
        error: 'Не удалось загрузить отчёт.',
        empty: 'В этом периоде посещаемости нет.',
        employee: 'Сотрудник',
        code: 'Код',
        totalDays: 'Дней отмечено',
        attendedDays: 'Присутствовал',
        absentDays: 'Отсутствовал',
        workedHours: 'Отработанные часы',
        attendanceRate: 'Явка',
        totals: 'Итоги по персоналу',
        month: 'Месяц',
        department: 'Подразделение',
        allDepartments: 'Все подразделения и склады',
        recorded: 'Отмечено дней',
        averageRate: 'Средняя явка',
        employeesCovered: 'Сотрудников в отчёте',
      },
      leave: {
        eyebrow: 'Время и посещаемость',
        title: 'Отпуска',
        description: 'Реестр отпусков на основе записей посещаемости со статусом «отпуск» и использование отпусков по сотрудникам.',
        period: 'Период',
        loading: 'Загрузка отпусков…',
        error: 'Не удалось загрузить отпуска.',
        empty: 'В этом периоде отпусков нет.',
        onLeaveToday: 'В отпуске сегодня',
        noOnLeaveToday: 'Сегодня в отпуске никого нет.',
        summaryTitle: 'Использование отпусков по сотрудникам',
        registerTitle: 'Реестр отпусков',
        newLeave: 'Оформить отпуск',
        month: 'Месяц',
        employee: 'Сотрудник',
        department: 'Подразделение',
        days: 'Дней отпуска',
        lastLeave: 'Последний день',
        date: 'Дата',
        from: 'С',
        to: 'По',
        notes: 'Примечания',
        record: 'Оформить',
        recordTitle: 'Оформить отпуск',
        recordHint: 'На каждый день создаётся запись посещаемости со статусом «отпуск».',
        created: 'Отпуск оформлен на {count} дн.',
        createdPartial: 'Отпуск оформлен на {count} дн. Пропущено {skipped} дн. — записи уже существуют.',
        noneCreated: 'На все выбранные дни записи уже есть.',
        invalidEmployee: 'Выберите сотрудника.',
        invalidRange: 'Выберите корректный период отпуска.',
        deleteConfirm: 'Удалить этот день отпуска?',
        deleted: 'День отпуска удалён.',
        leaveDays: 'Дней отпуска в периоде',
        employeesOnLeave: 'Сотрудников в отпуске',
        daysUsed: 'Использовано дней',
        noPermission: 'Согласование не требуется — отпуск фиксируется прямо в посещаемости.',
      },
      overtime: {
        eyebrow: 'Время и посещаемость',
        title: 'Сверхурочные и рабочие часы',
        description: 'Записи рабочих часов со сверхурочными, рассчитанными по превышению стандартного рабочего дня.',
        standardDay: 'Стандартный рабочий день',
        standardHint: 'Часы свыше этого порога считаются сверхурочными.',
        hours: 'часов',
        loading: 'Загрузка рабочих часов…',
        error: 'Не удалось загрузить рабочие часы.',
        empty: 'В этом периоде записей рабочих часов нет.',
        totalHours: 'Часов внесено',
        overtimeHours: 'Сверхурочные часы',
        employeesAffected: 'Сотрудников со сверхурочными',
        overtimeEntries: 'Дней со сверхурочными',
        ledgerTitle: 'Сверхурочные по дням',
        summaryTitle: 'Сверхурочные по сотрудникам',
        entriesTitle: 'Последние записи рабочих часов',
        employee: 'Сотрудник',
        department: 'Подразделение',
        workDate: 'Рабочая дата',
        entries: 'Записи',
        dayHours: 'Часы за день',
        overtime: 'Сверхурочные',
        task: 'Задача',
        entryType: 'Тип записи',
        billAmount: 'Сумма к оплате',
        logHours: 'Внести рабочие часы',
        logTitle: 'Внести рабочие часы',
        created: 'Рабочие часы внесены.',
        deleted: 'Запись удалена.',
        invalidEmployee: 'Выберите сотрудника.',
        invalidDate: 'Выберите рабочую дату.',
        invalidTask: 'Введите название задачи.',
        invalidHours: 'Часы должны быть больше нуля.',
        deleteConfirm: 'Удалить эту запись рабочих часов?',
        noOvertime: 'В этом периоде сверхурочных нет.',
        cycleUnit: 'циклов',
        totalOvertime: 'Всего сверхурочных',
        noEntries: 'Записей пока нет.',
        month: 'Месяц',
      },
      picker: {
        employeeTitle: 'Выбор сотрудника',
        employeeEyebrow: 'Активные сотрудники',
        employeeSearch: 'Поиск сотрудников',
        employeePlaceholder: 'Поиск по имени, коду или подразделению',
        employeeEmpty: 'Сотрудники не найдены.',
        employeeLoading: 'Загрузка сотрудников…',
        departmentTitle: 'Выбор подразделения или склада',
        departmentEyebrow: 'Подразделения и склады',
        departmentSearch: 'Поиск подразделений и складов',
        departmentPlaceholder: 'Поиск по названию или коду',
        departmentEmpty: 'Подразделения и склады не найдены.',
        departmentLoading: 'Загрузка подразделений и складов…',
        typeFilter: 'Тип',
        typeAll: 'Все типы',
        typeDepartment: 'Подразделение',
        typeInternal: 'Внутренний отдел',
        typeWarehouse: 'Склад',
        supervisorTitle: 'Выбор руководителя',
        supervisorEyebrow: 'Сотрудники',
        supervisorSearch: 'Поиск руководителя',
        supervisorPlaceholder: 'Поиск по имени или коду',
        supervisorEmpty: 'Сотрудники не найдены.',
        supervisorSelf: 'Сотрудник не может быть своим руководителем.',
        page: 'Страница {page} из {total}',
        pageRange: 'Страница {page} из {total} · {count} записей',
        close: 'Закрыть выбор',
        retry: 'Повторить',
        previous: 'Назад',
        next: 'Вперёд',
        cancel: 'Отмена',
      },
    },
  },
  uz: {
    hr: {
      appTitle: 'Kadrlar',
      appDescription: 'Xodimlar, davomat, ta’til va qo‘shimcha ish vaqti.',
      nav: {
        overview: 'Umumiy ko‘rinish',
        employees: 'Xodimlar',
        people: 'Kadrlar',
        time: 'Vaqt va davomat',
        attendance: 'Davomat',
        attendanceReport: 'Oylik hisobot',
        leave: 'Ta’til',
        overtime: 'Qo‘shimcha vaqt',
      },
      common: {
        actions: 'Amallar',
        open: 'Ochish',
        cancel: 'Bekor qilish',
        save: 'Saqlash',
        saving: 'Saqlanmoqda…',
        create: 'Yaratish',
        edit: 'Tahrirlash',
        delete: 'O‘chirish',
        retry: 'Qayta urinish',
        refresh: 'Yangilash',
        loading: 'Yuklanmoqda…',
        empty: 'Ma’lumot yo‘q.',
        search: 'Qidirish',
        searchPlaceholder: 'Ism, kod yoki lavozim bo‘yicha qidirish…',
        allDepartments: 'Barcha bo‘lim va omborlar',
        allTypes: 'Barcha turlar',
        allStatuses: 'Barcha holatlar',
        apply: 'Qo‘llash',
        reset: 'Tozalash',
        department: 'Bo‘lim',
        employee: 'Xodim',
        employeeCode: 'Tabel raqami',
        jobTitle: 'Lavozim',
        status: 'Holat',
        date: 'Sana',
        from: 'Dan',
        to: 'Gacha',
        notes: 'Izohlar',
        hours: 'Soat',
        cycles: 'Sikl',
        amount: 'Summa',
        currency: 'Valyuta',
        total: '{count} qator',
        page: '{page} / {total} sahifa',
        previous: 'Oldingi',
        next: 'Keyingi',
        back: 'Orqaga',
        close: 'Yopish',
        optional: 'majburiy emas',
        yes: 'Ha',
        no: 'Yo‘q',
        active: 'Faol',
        inactive: 'Nofaol',
        all: 'Barchasi',
        none: 'Yo‘q',
        noPermission: 'Bu amal uchun ruxsatingiz yo‘q.',
        invalidRange: 'Boshlanish sanasi tugash sanasidan keyin bo‘lishi mumkin emas.',
      },
      employeeType: {
        office: 'Ofis',
        production: 'Ishlab chiqarish',
        mixed: 'Aralash',
      },
      billingMode: {
        hourly: 'Soatbay',
        production_cycle: 'Ishlab chiqarish sikli uchun',
        fixed_salary: 'Belgilangan oylik',
        fixed_task: 'Belgilangan topshiriq',
      },
      attendanceStatus: {
        present: 'Kelgan',
        absent: 'Kelmagan',
        late: 'Kechikkan',
        leave: 'Ta’tilda',
        remote: 'Masofadan',
        half_day: 'Yarim kun',
      },
      entryType: {
        office_task: 'Ofis topshirig‘i',
        production_cycle: 'Ishlab chiqarish sikli',
        admin_task: 'Ma’muriy topshiriq',
      },
      billingUnit: {
        hour: 'Soat uchun',
        cycle: 'Sikl uchun',
        fixed: 'Belgilangan summa',
      },
      overview: {
        eyebrow: 'Kadrlar',
        title: 'Umumiy ko‘rinish',
        description: 'Xodimlar soni, bugungi davomat holati va joriy oyda kiritilgan ish soatlari.',
        loading: 'Umumiy ko‘rinish yuklanmoqda…',
        error: 'Umumiy ko‘rinishni yuklab bo‘lmadi.',
        headcount: 'Xodimlar',
        active: 'Faol xodimlar',
        departments: 'Bo‘limlar',
        presentToday: 'Bugun kelgan',
        absentToday: 'Bugun kelmagan',
        onLeaveToday: 'Bugun ta’tilda',
        hoursThisMonth: 'Oylik soatlar',
        overtimeHours: 'Qo‘shimcha soatlar',
        todaySnapshot: 'Bugungi davomat',
        noToday: 'Bugun uchun davomat hali kiritilmagan.',
        topPerformers: 'Oy davomida eng ko‘p soat',
        noPerformers: 'Bu oyda ish soati kiritilmagan.',
        quickActions: 'Tezkor amallar',
        addEmployee: 'Xodim qo‘shish',
        openAttendance: 'Davomat kiritish',
        openLeave: 'Ta’til reestri',
        openOvertime: 'Qo‘shimcha vaqt va soatlar',
        records: 'yozuv',
      },
      employees: {
        eyebrow: 'Kadrlar',
        title: 'Xodimlar',
        description: 'Bandlik turi, bo‘lim va hisob-kitob sozlamalari bilan kadrlar reestri.',
        newEmployee: 'Yangi xodim',
        loading: 'Xodimlar yuklanmoqda…',
        error: 'Xodimlarni yuklab bo‘lmadi.',
        empty: 'Filtrlarga mos xodim topilmadi.',
        name: 'Ism',
        code: 'Kod',
        department: 'Bo‘lim',
        jobTitle: 'Lavozim',
        type: 'Tur',
        billing: 'Hisob-kitob',
        hireDate: 'Ishga kirgan',
        status: 'Holat',
        view: 'Ochish',
        edit: 'Tahrirlash',
        delete: 'O‘chirish',
        deleteConfirm: 'Xodim o‘chirilsinmi? Davomat va tabel yozuvlari ham o‘chiriladi.',
        totals: '{total} xodim · {active} faol',
      },
      employeeForm: {
        createTitle: 'Yangi xodim',
        createDescription: 'Bandlik va hisob-kitob sozlamalari bilan kadrlar yozuvini yarating.',
        editTitle: 'Xodimni tahrirlash',
        editDescription: 'Kadrlar, aloqa va hisob-kitob ma’lumotlarini yangilang.',
        sectionIdentity: 'Bandlik',
        sectionContact: 'Aloqa',
        sectionPersonal: 'Shaxsiy ma’lumot va hujjatlar',
        sectionCompensation: 'Hisob-kitob sozlamalari',
        codeAuto: 'Tabel raqami {code} avtomatik beriladi.',
        firstName: 'Ism',
        lastName: 'Familiya',
        middleName: 'Otasining ismi',
        department: 'Bo‘lim / ombor',
        departmentHint: 'Bo‘limlar, ichki bo‘limlar va omborlar.',
        supervisor: 'Rahbar',
        jobTitle: 'Lavozim',
        employeeType: 'Bandlik turi',
        hireDate: 'Ishga qabul sanasi',
        active: 'Faol xodim',
        email: 'Elektron pochta',
        phone: 'Telefon',
        homeAddress: 'Uy manzili',
        emergencyName: 'Favqulodda aloqa',
        emergencyNumber: 'Favqulodda telefon',
        birthday: 'Tug‘ilgan sana',
        passport: 'Pasport raqami',
        taxId: 'STIR',
        bankCard: 'Bank karta raqami',
        billingMode: 'Hisob-kitob turi',
        hourlyRate: 'Soatbay stavka',
        cycleRate: 'Sikl uchun stavka',
        fixedSalary: 'Belgilangan oylik',
        currency: 'Valyuta',
        currencyFallback: 'Valyuta kodi',
        currencyHint: 'Faol valyutaning uch harfli ISO kodi. Kompaniya valyutasidan foydalanish uchun bo‘sh qoldiring.',
        currenciesUnavailable: 'Valyutalar ro‘yxati yuklanmadi — uch harfli kodni qo‘lda kiriting.',
        photo: 'Surat',
        photoHint: 'JPG, PNG, WebP yoki GIF, 5 MB gacha.',
        photoChoose: 'Surat tanlash',
        photoRemove: 'Suratni o‘chirish',
        photoInvalidType: 'Faqat JPG, PNG, WebP yoki GIF rasmlar qo‘llanadi.',
        photoTooLarge: 'Surat hajmi 5 MB dan oshmasligi kerak.',
        submitCreate: 'Xodim yaratish',
        submitUpdate: 'O‘zgarishlarni saqlash',
        created: 'Xodim yaratildi.',
        updated: 'Xodim saqlandi.',
        invalidRequired: 'Tabel raqami, ism, familiya, bandlik turi va hisob-kitob turi majburiy.',
        invalidRate: 'Stavkalar noldan kichik bo‘lmasligi kerak.',
        supervisorSelf: 'Xodim o‘ziga rahbar bo‘la olmaydi.',
      },
      employeeDetail: {
        eyebrow: 'Xodim',
        back: 'Xodimlar ro‘yxatiga',
        loading: 'Xodim yuklanmoqda…',
        error: 'Xodimni yuklab bo‘lmadi.',
        notFound: 'Xodim topilmadi.',
        profile: 'Profil',
        employment: 'Bandlik',
        contact: 'Aloqa',
        personal: 'Shaxsiy ma’lumot va hujjatlar',
        compensation: 'Hisob-kitob',
        linkedUser: 'Tizim foydalanuvchisi',
        noLinkedUser: 'Tizim foydalanuvchisiga bog‘lanmagan',
        supervisor: 'Rahbar',
        noSupervisor: 'Rahbar tayinlanmagan',
        hireDate: 'Ishga qabul sanasi',
        createdAt: 'Yozuv yaratilgan',
        edit: 'Xodimni tahrirlash',
        delete: 'O‘chirish',
        deleteConfirm: 'Xodim o‘chirilsinmi? Bu amalni qaytarib bo‘lmaydi.',
        deleteTitle: 'Xodimni o‘chirish',
        actionAttendance: 'Davomat',
        actionLeave: 'Ta’til',
        actionHours: 'Ish soati',
        actionsLabel: 'Xodim amallari',
        tabProfile: 'Profil',
        tabAttendance: 'Davomat',
        tabHours: 'Ish soati',
        tabDocuments: 'Hujjatlar',
        tabsLabel: 'Xodim bo‘limlari',
        recordAttendance: 'Davomat kiritish',
        recordLeave: 'Ta’til kiritish',
        recordOvertime: 'Ish soatini kiritish',
        attendanceSummary: 'Bu oydagi davomat',
        timesheetSummary: 'Bu oydagi soatlar',
        recentAttendance: 'So‘nggi davomat',
        recentTimesheets: 'So‘nggi ish soatlari',
        noAttendance: 'Bu oyda davomat yozuvi yo‘q.',
        noTimesheets: 'Bu oyda ish soati kiritilmagan.',
        daysAttended: 'Kelgan kunlar',
        daysAbsent: 'Kelmagan kunlar',
        attendanceRate: 'Davomat darajasi',
        workedHours: 'Ishlangan soat',
        totalHours: 'Jami soat',
        entries: 'Yozuvlar',
        billAmount: 'Hisob summasi',
        rates: 'Stavkalar',
        hourlyRate: 'Soatbay',
        cycleRate: 'Sikl uchun',
        fixedSalary: 'Oylik',
      },
      employeeFiles: {
        title: 'Hujjatlar',
        description: 'Xodimga tegishli hujjat nusxalari: pasport, shartnoma, sertifikatlar, tibbiy ko‘rik.',
        count: '{count} fayl',
        category: 'Turkum',
        file: 'Fayl',
        upload: 'Yuklash',
        uploading: 'Yuklanmoqda…',
        uploadHint: 'Rasm, PDF, Word yoki Excel 20 MB gacha.',
        uploaded: 'Hujjat yuklandi.',
        deleted: 'Hujjat o‘chirildi.',
        delete: 'O‘chirish',
        download: 'Yuklab olish',
        deleteConfirm: '“{name}” o‘chirilsinmi? Fayl serverdan o‘chiriladi.',
        tooLarge: 'Fayl hajmi 20 MB dan oshmasligi kerak.',
        noFile: 'Avval faylni tanlang.',
        empty: 'Hozircha hujjat yuklanmagan.',
        loading: 'Hujjatlar yuklanmoqda…',
        name: 'Fayl',
        size: 'Hajmi',
        uploadedBy: 'Yuklagan',
        uploadedAt: 'Yuklangan',
        categoryPassport: 'Pasport',
        categoryContract: 'Shartnoma',
        categoryCertificate: 'Sertifikat',
        categoryMedical: 'Tibbiy ko‘rik',
        categoryOther: 'Boshqa',
      },
      attendance: {
        eyebrow: 'Vaqt va davomat',
        title: 'Davomat',
        description: 'Kirish/chiqish vaqtlari va ishlangan daqiqalar bilan kunlik davomat reestri.',
        newRecord: 'Davomat kiritish',
        loading: 'Davomat yuklanmoqda…',
        error: 'Davomatni yuklab bo‘lmadi.',
        empty: 'Filtrlarga mos davomat yozuvi yo‘q.',
        employee: 'Xodim',
        department: 'Bo‘lim',
        date: 'Sana',
        status: 'Holat',
        checkIn: 'Kirish',
        checkOut: 'Chiqish',
        worked: 'Ishlangan',
        minutes: 'Ishlangan daqiqa',
        notes: 'Izohlar',
        edit: 'Tahrirlash',
        delete: 'O‘chirish',
        deleteConfirm: 'Davomat yozuvi o‘chirilsinmi?',
        formTitleCreate: 'Davomat kiritish',
        formTitleEdit: 'Davomatni tahrirlash',
        created: 'Davomat kiritildi.',
        updated: 'Davomat yangilandi.',
        deleted: 'Davomat yozuvi o‘chirildi.',
        invalidEmployee: 'Xodimni tanlang.',
        invalidDate: 'Davomat sanasini tanlang.',
        duplicate: 'Bu xodim va sana uchun yozuv allaqachon mavjud.',
        summaryPresent: 'Kelgan',
        summaryAbsent: 'Kelmagan',
        summaryLate: 'Kechikkan',
        summaryLeave: 'Ta’tilda',
        summaryRemote: 'Masofadan',
        summaryHalfDay: 'Yarim kun',
        filterStatus: 'Holat',
        rangeFrom: 'Boshlanish',
        rangeTo: 'Tugash',
        minutesHint: 'Daqiqalar shundayligicha saqlanadi; bo‘sh qoldirsangiz kirish/chiqish vaqti ishlatiladi.',
        totals: 'Davrda {count} yozuv',
      },
      attendanceReport: {
        eyebrow: 'Vaqt va davomat',
        title: 'Oylik davomat hisoboti',
        description: 'Tanlangan davr uchun xodimlar bo‘yicha davomat jamlanmasi va davomat darajasi.',
        loading: 'Hisobot yuklanmoqda…',
        error: 'Hisobotni yuklab bo‘lmadi.',
        empty: 'Bu davrda davomat yo‘q.',
        employee: 'Xodim',
        code: 'Kod',
        totalDays: 'Qayd etilgan kun',
        attendedDays: 'Kelgan',
        absentDays: 'Kelmagan',
        workedHours: 'Ishlangan soat',
        attendanceRate: 'Davomat darajasi',
        totals: 'Kadrlar bo‘yicha jamlanma',
        month: 'Oy',
        department: 'Bo‘lim',
        allDepartments: 'Barcha bo‘lim va omborlar',
        recorded: 'Qayd etilgan kun',
        averageRate: 'O‘rtacha davomat',
        employeesCovered: 'Qamrab olingan xodim',
      },
      leave: {
        eyebrow: 'Vaqt va davomat',
        title: 'Ta’til',
        description: '“Ta’tilda” holatidagi davomat yozuvlaridan olinadigan ta’til reestri va xodimlar bo‘yicha ta’til sarfi.',
        period: 'Davr',
        loading: 'Ta’tillar yuklanmoqda…',
        error: 'Ta’tillarni yuklab bo‘lmadi.',
        empty: 'Bu davrda ta’til yozuvi yo‘q.',
        onLeaveToday: 'Bugun ta’tilda',
        noOnLeaveToday: 'Bugun hech kim ta’tilda emas.',
        summaryTitle: 'Xodimlar bo‘yicha ta’til sarfi',
        registerTitle: 'Ta’til reestri',
        newLeave: 'Ta’til kiritish',
        month: 'Oy',
        employee: 'Xodim',
        department: 'Bo‘lim',
        days: 'Ta’til kunlari',
        lastLeave: 'Oxirgi ta’til kuni',
        date: 'Sana',
        from: 'Dan',
        to: 'Gacha',
        notes: 'Izohlar',
        record: 'Kiritish',
        recordTitle: 'Ta’til kiritish',
        recordHint: 'Har bir kun uchun “ta’tilda” holatidagi davomat yozuvi yaratiladi.',
        created: '{count} kun uchun ta’til kiritildi.',
        createdPartial: '{count} kun uchun ta’til kiritildi. {skipped} kunda yozuv allaqachon bor edi.',
        noneCreated: 'Tanlangan kunlarning barchasida yozuv mavjud.',
        invalidEmployee: 'Xodimni tanlang.',
        invalidRange: 'To‘g‘ri ta’til davrini tanlang.',
        deleteConfirm: 'Bu ta’til kuni o‘chirilsinmi?',
        deleted: 'Ta’til kuni o‘chirildi.',
        leaveDays: 'Davrdagi ta’til kunlari',
        employeesOnLeave: 'Ta’tildagi xodimlar',
        daysUsed: 'Ishlatilgan kunlar',
        noPermission: 'Tasdiqlash talab qilinmaydi — ta’til to‘g‘ridan-to‘g‘ri davomatga yoziladi.',
      },
      overtime: {
        eyebrow: 'Vaqt va davomat',
        title: 'Qo‘shimcha vaqt va ish soatlari',
        description: 'Standart ish kunidan ortiq soatlar qo‘shimcha vaqt sifatida hisoblanadigan ish soati yozuvlari.',
        standardDay: 'Standart ish kuni',
        standardHint: 'Bu chegaradan ortiq kunlik soatlar qo‘shimcha vaqt hisoblanadi.',
        hours: 'soat',
        loading: 'Ish soatlari yuklanmoqda…',
        error: 'Ish soatlarini yuklab bo‘lmadi.',
        empty: 'Bu davrda ish soati yozuvi yo‘q.',
        totalHours: 'Kiritilgan soat',
        overtimeHours: 'Qo‘shimcha soatlar',
        employeesAffected: 'Qo‘shimcha vaqti bor xodimlar',
        overtimeEntries: 'Qo‘shimcha vaqt kunlari',
        ledgerTitle: 'Kunlik qo‘shimcha vaqt',
        summaryTitle: 'Xodimlar bo‘yicha qo‘shimcha vaqt',
        entriesTitle: 'So‘nggi ish soati yozuvlari',
        employee: 'Xodim',
        department: 'Bo‘lim',
        workDate: 'Ish sanasi',
        entries: 'Yozuvlar',
        dayHours: 'Kunlik soat',
        overtime: 'Qo‘shimcha',
        task: 'Topshiriq',
        entryType: 'Yozuv turi',
        billAmount: 'Hisob summasi',
        logHours: 'Ish soatini kiritish',
        logTitle: 'Ish soatini kiritish',
        created: 'Ish soati kiritildi.',
        deleted: 'Yozuv o‘chirildi.',
        invalidEmployee: 'Xodimni tanlang.',
        invalidDate: 'Ish sanasini tanlang.',
        invalidTask: 'Topshiriq nomini kiriting.',
        invalidHours: 'Soat noldan katta bo‘lishi kerak.',
        deleteConfirm: 'Bu ish soati yozuvi o‘chirilsinmi?',
        noOvertime: 'Bu davrda qo‘shimcha vaqt yo‘q.',
        cycleUnit: 'sikl',
        totalOvertime: 'Jami qo‘shimcha vaqt',
        noEntries: 'Hozircha yozuv yo‘q.',
        month: 'Oy',
      },
      picker: {
        employeeTitle: 'Xodimni tanlash',
        employeeEyebrow: 'Faol xodimlar',
        employeeSearch: 'Xodimlarni qidirish',
        employeePlaceholder: 'Ism, kod yoki bo‘lim bo‘yicha qidirish',
        employeeEmpty: 'Xodim topilmadi.',
        employeeLoading: 'Xodimlar yuklanmoqda…',
        departmentTitle: 'Bo‘lim yoki omborni tanlash',
        departmentEyebrow: 'Bo‘limlar va omborlar',
        departmentSearch: 'Bo‘lim va omborlarni qidirish',
        departmentPlaceholder: 'Nomi yoki kodi bo‘yicha qidirish',
        departmentEmpty: 'Bo‘lim yoki ombor topilmadi.',
        departmentLoading: 'Bo‘lim va omborlar yuklanmoqda…',
        typeFilter: 'Turi',
        typeAll: 'Barcha turlar',
        typeDepartment: 'Bo‘lim',
        typeInternal: 'Ichki bo‘lim',
        typeWarehouse: 'Ombor',
        supervisorTitle: 'Rahbarni tanlash',
        supervisorEyebrow: 'Xodimlar',
        supervisorSearch: 'Rahbarni qidirish',
        supervisorPlaceholder: 'Ism yoki kod bo‘yicha qidirish',
        supervisorEmpty: 'Xodim topilmadi.',
        supervisorSelf: 'Xodim o‘ziga rahbar bo‘la olmaydi.',
        page: '{page} / {total} sahifa',
        pageRange: '{page}/{total} sahifa · {count} yozuv',
        close: 'Tanlashni yopish',
        retry: 'Qayta urinish',
        previous: 'Oldingi',
        next: 'Keyingi',
        cancel: 'Bekor qilish',
      },
    },
  },
}
